// ==========================================
// FILE: \src\screens\HomeScreen.js
// ==========================================

import React, { useState, useEffect } from 'react';
import { 
  View, Text, StyleSheet, TouchableOpacity, ActivityIndicator, 
  ScrollView, TextInput, KeyboardAvoidingView, Platform, Modal, Keyboard
} from 'react-native';
import { doc, getDoc } from 'firebase/firestore';
import { signOut, getAuth } from 'firebase/auth';
import { auth, db } from '../config/firebase';
import { useWineContext } from '../context/WineContext'; 
import { callGemini } from '../utils/geminiClient'; 
import EnergyHeader from '../components/EnergyHeader';

import OutOfEnergyModal from '../components/OutOfEnergyModal';
import RewardedAdModal from '../components/RewardedAdModal';

// Importazione dell'SDK Google SignIn per svuotare la cache su dispositivi mobili
import { GoogleSignin } from '@react-native-google-signin/google-signin';

import { 
  Wine, Utensils, Search, User, BookOpen, 
  PlusCircle, Sparkles, ChevronRight, AlertCircle, ShoppingBag, 
  CheckCircle2, ArrowRight, LogOut, PieChart, Map, LayoutGrid 
} from 'lucide-react-native';

export default function HomeScreen({ navigation }) {
  // Estraiamo in modo sicuro la funzione t e il profilo globale per l'internazionalizzazione
  const { presentWines, isLoadingWines, consumeEnergy, addEnergy, userProfile, getUnplacedWines, locations, t } = useWineContext();
  const isPremium = userProfile?.isPremium || false;

  const [searchQuery, setSearchQuery] = useState('');
  const [searchMode, setSearchMode] = useState('food-to-wine'); 
  const [isSearching, setIsSearching] = useState(false);
  const [aiResponse, setAiResponse] = useState(null); 
  const [activeTab, setActiveTab] = useState('cantina'); 
  
  const [customAlert, setCustomAlert] = useState({ visible: false, title: '', message: '', type: 'info', onConfirm: null });

  const [showOutOfEnergy, setShowOutOfEnergy] = useState(false);
  const [showAdModal, setShowAdModal] = useState(false);

  const [displayName, setDisplayName] = useState('');
  const user = auth.currentUser;

  const unplacedCount = getUnplacedWines ? getUnplacedWines().reduce((acc, w) => acc + (w.missingToPlace || 0), 0) : 0;

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) return;
      try {
        const docRef = doc(db, 'users', user.uid);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists() && docSnap.data().name) {
          setDisplayName(docSnap.data().name);
        } else {
          setDisplayName(user.email.split('@')[0]);
        }
      } catch (error) {
        setDisplayName(user.email?.split('@')[0] || '');
      }
    };

    fetchUserProfile();
  }, [user]);

  const showAppAlert = (title, message, type = 'info', onConfirm = null) => {
    Keyboard.dismiss(); 
    setCustomAlert({ visible: true, title, message, type, onConfirm });
  };

  const closeAppAlert = () => {
    setCustomAlert(prev => ({ ...prev, visible: false }));
  };

  const handleLogout = () => {
    Keyboard.dismiss();
    showAppAlert(
      t('home.buttons.logoutConfirmTitle'),
      t('home.buttons.logoutConfirmMsg'),
      "confirm",
      async () => {
        closeAppAlert();
        try {
          // Identifichiamo l'istanza Auth reattiva corretta per la piattaforma corrente
          const activeAuth = Platform.OS === 'web' ? getAuth() : auth;

          // 1. Gestione ibrida e isolata dei Provider: pulizia pre-logout senza blocchi di runtime
          if (Platform.OS === 'web') {
            try {
              // Su Web impostiamo il flag per forzare il prompt di selezione account al prossimo tentativo
              localStorage.setItem('vinologic_force_google_prompt', 'true');
            } catch (webStorageError) {
              console.warn("[VinoLogic Web] Errore nell'impostazione del prompt locale:", webStorageError);
            }
          } else {
            try {
              // Su Mobile (iOS/Android) verifichiamo la presenza di una sessione attiva prima di invocare il signOut nativo
              const isGoogleSignedIn = await GoogleSignin.isSignedIn();
              if (isGoogleSignedIn) {
                await GoogleSignin.signOut();
                console.log("[VinoLogic Mobile] Sessione memorizzata nella cache nativa di Google azzerata.");
              }
            } catch (googleError) {
              // Silenziamo l'errore: se l'utente accede con mail/password, il fallimento controllato dell'SDK di Google non deve interrompere l'azione
              console.warn("[VinoLogic Mobile] Errore controllato e bypassato nel signOut nativo di Google:", googleError);
            }
          }

          // 2. Disconnessione atomica e definitiva da Firebase Authentication
          await signOut(activeAuth);
          console.log("[VinoLogic] Disconnessione eseguita con successo su Firebase.");
        } catch (error) {
          console.error("[VinoLogic] Errore critico nel logout di Firebase:", error);
          showAppAlert(t('global.error'), t('home.alerts.logoutErrorMsg'), "error");
        }
      }
    );
  };

  // --- AZIONE: LOCALIZZA VINO DAL SOMMELIER (Costo: 1⚡) ---
  const handleLocateWine = async (wineId) => {
    if (!isPremium && userProfile?.energia < 1) {
      setShowOutOfEnergy(true);
      return;
    }

    const hasEnergy = await consumeEnergy(1);
    if (!hasEnergy) {
      setShowOutOfEnergy(true);
      return;
    }

    const targetLoc = locations?.find(l => l.slots?.some(s => s.wineId === wineId));
    const targetLocId = targetLoc ? targetLoc.id : null;

    navigation.navigate('Caveau', { highlightWineId: wineId, targetLocId });
  };

  const handleAISearch = async () => {
    Keyboard.dismiss(); 
    if (!searchQuery.trim()) return;
    if (isSearching) return;
    
    setIsSearching(true); 
    setAiResponse(null);

    if (!isPremium && userProfile?.energia < 1) {
      setShowOutOfEnergy(true);
      setIsSearching(false);
      return;
    }

    const hasEnergy = await consumeEnergy(1);
    if (!hasEnergy) {
      setShowOutOfEnergy(true);
      setIsSearching(false);
      return; 
    }

    try {
      const cellarContext = presentWines.map(w => ({
        id: w.id,
        name: w.name,
        type: w.type,
        vitigno: w.technicalSheet?.vitigno || 'Sconosciuto',
        regione: w.technicalSheet?.regione || 'Sconosciuta'
      }));

      // Identifichiamo esplicitamente il nome della lingua attiva nel contesto utente
      const langNames = { it: "Italiano", en: "Inglese (English)", fr: "Francese (Français)" };
      const targetLanguageName = langNames[userProfile?.language || 'it'] || "Italiano";

      let promptText = "";
      
      if (searchMode === 'food-to-wine') {
        promptText = `
        Sei un Master Sommelier stellato. L'utente mangerà: "${searchQuery}".
        Questa è la sua cantina attuale in formato JSON: ${JSON.stringify(cellarContext)}
        
        REGOLE ASSOLUTE:
        1. LINGUA: Tutti i testi descrittivi e motivazionali generati ("reason", "noMatchReason", "description") DEVONO essere scritti rigorosamente in lingua ${targetLanguageName}.
        2. CANTINA: Analizza i vini dell'utente. Cerca il miglior abbinamento. Se ne trovi uno ottimo, suggeriscilo in bestMatch. Se ce n'è un altro di stile diverso ma valido, mettilo in alternativeMatch.
           Se NESSUN vino in cantina si abbina bene (es. ha solo rossi corposi e mangia ostriche), DEVI impostare "hasMatch" a false e spiegare perché non ha vini adatti in "noMatchReason", senza forzare abbinamenti inadeguati.
        3. ACQUISTO: Indipendentemente dalla cantina, suggerisci 2 tipologie di vino (es. "Chablis" o "Franciacorta") perfette per questo piatto. Devono essere di 2 stili diversi. Motiva la scelta.
        
        RISPONDI SOLO ED ESCLUSIVAMENTE CON UN JSON VALIDO CON QUESTA STRUTTURA:
        {
          "type": "food-to-wine",
          "cantina": {
            "hasMatch": true,
            "noMatchReason": "Spiegazione in lingua ${targetLanguageName}",
            "bestMatch": { "id": "id_dal_json", "name": "Nome vino", "reason": "Motivazione in lingua ${targetLanguageName}" },
            "alternativeMatch": { "id": "id_dal_json", "name": "Nome vino", "reason": "Motivazione in lingua ${targetLanguageName}" } 
          },
          "acquisto": {
            "suggestion1": { "type": "Tipologia 1", "description": "Motivazione in lingua ${targetLanguageName}" },
            "suggestion2": { "type": "Tipologia 2", "description": "Motivazione in lingua ${targetLanguageName}" }
          }
        }`;
      } else {
        promptText = `
        Sei un Master Sommelier stellato. L'utente ha scelto di bere: "${searchQuery}".
        Suggeriscimi 2 o 3 piatti perfetti da mangiare in abbinamento, spiegando brevemente perché si sposano bene. 
        
        REGOLE ASSOLUTE:
        1. LINGUA: Tutti i testi descrittivi generati ("recommendationText", "name", "reason") DEVONO essere scritti rigorosamente in lingua ${targetLanguageName}.
        
        RISPONDI SOLO ED ESCLUSIVAMENTE CON UN JSON VALIDO CON QUESTA STRUTTURA:
        {
          "type": "wine-to-food",
          "recommendationText": "Un breve commento introduttivo sul vino scelto in lingua ${targetLanguageName}.",
          "foodSuggestions": [
            { "name": "Nome del piatto", "reason": "Perché si abbina bene in lingua ${targetLanguageName}" }
          }
        }`;
      }

      const extractedData = await callGemini(promptText, true);

      setAiResponse(extractedData);
      if (extractedData.type === 'food-to-wine') setActiveTab('cantina');

    } catch (error) {
      if (!isPremium) await addEnergy(1);
      showAppAlert(t('global.error'), t('profile.alerts.saveErrorMsg'), "error");
    } finally {
      setIsSearching(false);
    }
  };

  const handleAdComplete = () => {
    setShowAdModal(false);
    addEnergy(5);
    showAppAlert(t('home.alerts.adSuccessTitle'), t('home.alerts.adSuccessMsg'), "success");
  };

  const renderFoodToWineResult = () => {
    if (!aiResponse || aiResponse.type !== 'food-to-wine') return null;
    const { cantina, acquisto } = aiResponse;

    return (
      <View style={styles.aiResultWrapper}>
        
        <View style={styles.tabContainer}>
          <TouchableOpacity style={[styles.tabBtn, activeTab === 'cantina' && styles.tabBtnActive]} onPress={() => setActiveTab('cantina')}>
            <Wine color={activeTab === 'cantina' ? "#fff" : "#64748b"} size={18} />
            <Text style={[styles.tabText, activeTab === 'cantina' && styles.tabTextActive]}>{t('home.results.fromCellar')}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[styles.tabBtn, activeTab === 'acquisto' && styles.tabBtnActiveBuy]} onPress={() => setActiveTab('acquisto')}>
            <ShoppingBag color={activeTab === 'acquisto' ? "#fff" : "#64748b"} size={18} />
            <Text style={[styles.tabText, activeTab === 'acquisto' && styles.tabTextActive]}>{t('home.results.toBuy')}</Text>
          </TouchableOpacity>
        </View>

        {activeTab === 'cantina' && (
          <View style={styles.tabContentArea}>
            {!cantina.hasMatch ? (
              <View style={styles.noMatchCard}>
                <AlertCircle color="#e11d48" size={32} />
                <Text style={styles.noMatchTitle}>{t('home.results.noMatchTitle')}</Text>
                <Text style={styles.noMatchText}>{cantina.noMatchReason}</Text>
              </View>
            ) : (
              <View style={styles.matchContainer}>
                {cantina.bestMatch && cantina.bestMatch.name && (
                  <View style={styles.wineCard}>
                    <View style={styles.matchBadge}><CheckCircle2 color="white" size={14} /><Text style={styles.matchBadgeText}>{t('home.results.bestChoice')}</Text></View>
                    <Text style={styles.wineCardTitle}>{cantina.bestMatch.name}</Text>
                    <Text style={styles.wineCardReason}>{cantina.bestMatch.reason}</Text>
                    
                    <View style={styles.actionButtonsRow}>
                      <TouchableOpacity style={styles.locateWineBtn} onPress={() => handleLocateWine(cantina.bestMatch.id)}>
                        <Map color="#475569" size={16} />
                        <Text style={styles.locateWineBtnText}>{t('home.results.locateAction')} (1⚡)</Text>
                      </TouchableOpacity>
                      <TouchableOpacity style={styles.openWineBtn} onPress={() => {
                          const matchedWine = presentWines.find(w => w.id === cantina.bestMatch.id);
                          if (matchedWine) navigation.navigate('Detail', { wine: matchedWine });
                        }}>
                        <Text style={styles.openWineBtnText}>{t('home.results.openSheet')}</Text>
                        <ArrowRight color="#e11d48" size={16} />
                      </TouchableOpacity>
                    </View>
                  </View>
                )}
                {cantina.alternativeMatch && cantina.alternativeMatch.name && (
                  <View style={[styles.wineCard, styles.altWineCard]}>
                    <Text style={styles.altBadgeText}>{t('home.results.alternative')}</Text>
                    <Text style={styles.wineCardTitle}>{cantina.alternativeMatch.name}</Text>
                    <Text style={styles.wineCardReason}>{cantina.alternativeMatch.reason}</Text>
                    
                    <View style={styles.actionButtonsRow}>
                      <TouchableOpacity style={styles.locateWineBtn} onPress={() => handleLocateWine(cantina.alternativeMatch.id)}>
                        <Map color="#475569" size={16} />
                        <Text style={styles.locateWineBtnText}>{t('home.results.locateAction')} (1⚡)</Text>
                      </TouchableOpacity>
                      <TouchableOpacity style={styles.openWineBtn} onPress={() => {
                          const matchedWine = presentWines.find(w => w.id === cantina.alternativeMatch.id);
                          if (matchedWine) navigation.navigate('Detail', { wine: matchedWine });
                        }}>
                        <Text style={styles.openWineBtnText}>{t('home.results.openSheet')}</Text>
                        <ArrowRight color="#e11d48" size={16} />
                      </TouchableOpacity>
                    </View>
                  </View>
                )}
              </View>
            )}
          </View>
        )}

        {activeTab === 'acquisto' && acquisto && (
          <View style={styles.tabContentArea}>
            {acquisto.suggestion1 && (
              <View style={styles.buyCard}>
                <View style={styles.buyIconWrap}><Wine color="#4f46e5" size={24} /></View>
                <Text style={styles.buyCardTitle}>{acquisto.suggestion1.type}</Text>
                <Text style={styles.wineCardReason}>{acquisto.suggestion1.description}</Text>
              </View>
            )}
            {acquisto.suggestion2 && (
              <View style={styles.buyCard}>
                <View style={styles.buyIconWrap}><Wine color="#4f46e5" size={24} /></View>
                <Text style={styles.buyCardTitle}>{acquisto.suggestion2.type}</Text>
                <Text style={styles.wineCardReason}>{acquisto.suggestion2.description}</Text>
              </View>
            )}
          </View>
        )}
      </View>
    );
  };

  const renderWineToFoodResult = () => {
    if (!aiResponse || aiResponse.type !== 'wine-to-food') return null;
    return (
      <View style={styles.aiResultContainer}>
        <Text style={styles.aiResultTitle}>{t('home.results.expertAdvice')}</Text>
        <Text style={styles.aiResultText}>{aiResponse.recommendationText}</Text>
        <Text style={styles.matchedTitle}>{t('home.results.perfectDishes')}</Text>
        {aiResponse.foodSuggestions?.map((food, idx) => (
          <View key={idx} style={styles.foodCard}>
            <View style={styles.foodIconBg}><Utensils color="#e11d48" size={20} /></View>
            <View style={styles.foodInfo}>
              <Text style={styles.foodName}>{food.name}</Text>
              <Text style={styles.foodReason}>{food.reason}</Text>
            </View>
          </View>
        ))}
      </View>
    );
  };

  return (
    <View style={styles.mainWrapper}>
      
      <View style={styles.topRedBanner}>
         <EnergyHeader />
      </View>

      <KeyboardAvoidingView style={styles.container} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView contentContainerStyle={styles.scrollContent} bounces={false} keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
          
          <View style={styles.pageHeaderCushion}>
            <View style={styles.headerTextWrap}>
              <Text style={styles.greeting}>{t('home.header.welcome')}</Text>
              <Text style={styles.emailText} numberOfLines={1}>{displayName || t('home.header.loadingName')}</Text>
            </View>
            
            <View style={styles.headerActions}>
              <TouchableOpacity onPress={() => navigation.navigate('Profile')} style={styles.profileButton}>
                <User color="#64748b" size={22} />
              </TouchableOpacity>
              <TouchableOpacity onPress={handleLogout} style={styles.logoutIconButton}>
                <LogOut color="#e11d48" size={22} />
              </TouchableOpacity>
            </View>
          </View>

          <View style={styles.heroSection}>
            <View style={styles.heroHeader}>
              <Sparkles color="#fef08a" size={28} />
              <Text style={styles.heroTitle}>{t('home.hero.title')}</Text>
            </View>
            <Text style={styles.heroSubtitle}>{t('home.hero.subtitle')}</Text>

            <View style={styles.toggleContainer}>
              <TouchableOpacity style={[styles.toggleBtn, searchMode === 'food-to-wine' && styles.toggleBtnActive]} onPress={() => { setSearchMode('food-to-wine'); setAiResponse(null); Keyboard.dismiss(); }}>
                <Utensils size={18} color={searchMode === 'food-to-wine' ? '#fff' : '#fecdd3'} />
                <Text style={[styles.toggleText, searchMode === 'food-to-wine' && styles.toggleTextActive]}>{t('home.hero.toggleEat')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.toggleBtn, searchMode === 'wine-to-food' && styles.toggleBtnActive]} onPress={() => { setSearchMode('wine-to-food'); setAiResponse(null); Keyboard.dismiss(); }}>
                <Wine size={18} color={searchMode === 'wine-to-food' ? '#fff' : '#fecdd3'} />
                <Text style={[styles.toggleText, searchMode === 'wine-to-food' && styles.toggleTextActive]}>{t('home.hero.toggleDrink')}</Text>
              </TouchableOpacity>
            </View>

            <View style={styles.searchBox}>
              <TextInput 
                style={styles.searchInput} 
                placeholder={searchMode === 'food-to-wine' ? t('home.hero.placeholderFood') : t('home.hero.placeholderWine')} 
                placeholderTextColor="#94a3b8" 
                value={searchQuery} 
                onChangeText={setSearchQuery} 
                onSubmitEditing={handleAISearch} 
                returnKeyType="search" 
                numberOfLines={1} 
              />
              <TouchableOpacity style={styles.searchButton} onPress={handleAISearch} disabled={isSearching}>
                {isSearching ? <ActivityIndicator color="#fff" size="small" /> : <><Search color="#fff" size={18} style={{marginRight: 4}} /><Text style={styles.searchButtonText}>(1⚡)</Text></>}
              </TouchableOpacity>
            </View>
          </View>

          {aiResponse && searchMode === 'food-to-wine' && renderFoodToWineResult()}
          {aiResponse && searchMode === 'wine-to-food' && renderWineToFoodResult()}

          <Text style={styles.sectionTitle}>{t('home.sections.management')}</Text>
          
          <View style={styles.gridContainer}>
            
            <TouchableOpacity style={styles.gridCardFull} onPress={() => navigation.navigate('AddWine')}>
              <View style={[styles.gridIconWrap, {backgroundColor: '#fff1f2', marginRight: 16}]}><PlusCircle color="#e11d48" size={26} /></View>
              <View style={{flex: 1}}>
                <Text style={styles.gridTitleFull}>{t('home.grid.addWineTitle')}</Text>
                <Text style={styles.gridSubFull}>{t('home.grid.addWineSub')}</Text>
              </View>
              <ChevronRight color="#cbd5e1" size={24} />
            </TouchableOpacity>

            <View style={styles.gridRow}>
              <TouchableOpacity style={styles.gridCardHalf} onPress={() => navigation.navigate('Caveau')}>
                <View style={styles.gridCardTop}>
                  <View style={[styles.gridIconWrap, {backgroundColor: '#fff1f2'}]}><LayoutGrid color="#e11d48" size={24} /></View>
                  {unplacedCount > 0 && <View style={styles.alertBadge}><Text style={styles.alertBadgeText}>{unplacedCount}</Text></View>}
                </View>
                <Text style={styles.gridTitle}>{t('home.grid.cellar3dTitle')}</Text>
                <Text style={styles.gridSub}>{t('home.grid.cellar3dSub')}</Text>
              </TouchableOpacity>

              <TouchableOpacity style={styles.gridCardHalf} onPress={() => navigation.navigate('Catalog')}>
                <View style={styles.gridCardTop}>
                  <View style={[styles.gridIconWrap, {backgroundColor: '#f1f5f9'}]}><BookOpen color="#0f172a" size={24} /></View>
                </View>
                <Text style={styles.gridTitle}>{t('home.grid.myWinesTitle')}</Text>
                <Text style={styles.gridSub}>{isLoadingWines ? t('home.grid.myWinesSubCalc') : `${presentWines.length} ${t('home.grid.myWinesSubLabels')}`}</Text>
              </TouchableOpacity>
            </View>

            <View style={styles.gridRow}>
              <TouchableOpacity style={styles.gridCardHalf} onPress={() => navigation.navigate('Stats')}>
                <View style={styles.gridCardTop}>
                  <View style={[styles.gridIconWrap, {backgroundColor: '#e0e7ff'}]}><PieChart color="#4f46e5" size={24} /></View>
                </View>
                <Text style={styles.gridTitle}>{t('home.grid.statsTitle')}</Text>
                <Text style={styles.gridSub}>{t('home.grid.statsSub')}</Text>
              </TouchableOpacity>

              <TouchableOpacity style={styles.gridCardHalf} onPress={() => navigation.navigate('Map')}>
                <View style={styles.gridCardTop}>
                  <View style={[styles.gridIconWrap, {backgroundColor: '#dcfce7'}]}><Map color="#16a34a" size={24} /></View>
                </View>
                <Text style={styles.gridTitle}>{t('home.grid.mapTitle')}</Text>
                <Text style={styles.gridSub}>{t('home.grid.mapSub')}</Text>
              </TouchableOpacity>
            </View>

          </View>

        </ScrollView>
      </KeyboardAvoidingView>

      <OutOfEnergyModal visible={showOutOfEnergy} onClose={() => setShowOutOfEnergy(false)} onWatchAd={() => { setShowOutOfEnergy(false); setShowAdModal(true); }} onSubscribe={() => { setShowOutOfEnergy(false); navigation.navigate('Paywall'); }} />
      <RewardedAdModal visible={showAdModal} onClose={() => setShowAdModal(false)} onReward={handleAdComplete} />

      <View>
        <Modal transparent={true} visible={customAlert.visible} animationType="fade">
          <View style={styles.modalOverlay}>
            <View style={styles.modalBox}>
              <View style={styles.modalHeader}>
                {customAlert.type === 'error' ? <AlertCircle color="#ef4444" size={28} /> : customAlert.type === 'confirm' ? <LogOut color="#e11d48" size={28} /> : customAlert.type === 'success' ? <CheckCircle2 color="#10b981" size={28} /> : <Sparkles color="#3b82f6" size={28} />}
                <Text style={styles.modalTitle}>{customAlert.title}</Text>
              </View>
              <Text style={styles.modalMessage}>{customAlert.message}</Text>
              <View style={styles.modalButtons}>
                {customAlert.type === 'confirm' && (
                  <TouchableOpacity style={styles.modalCancelBtn} onPress={closeAppAlert}><Text style={styles.modalCancelText}>{t('home.buttons.cancel')}</Text></TouchableOpacity>
                )}
                <TouchableOpacity style={[styles.modalConfirmBtn, customAlert.type === 'error' || customAlert.type === 'confirm' ? { backgroundColor: '#e11d48' } : customAlert.type === 'success' ? { backgroundColor: '#10b981' } : { backgroundColor: '#0f172a' }]} onPress={() => { if (customAlert.onConfirm) customAlert.onConfirm(); else closeAppAlert(); }}>
                  <Text style={styles.modalConfirmText}>{customAlert.type === 'confirm' ? t('home.buttons.exit') : t('home.buttons.dismiss')}</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  mainWrapper: { flex: 1, backgroundColor: '#f8fafc' },
  container: { flex: 1 },
  topRedBanner: { backgroundColor: '#e11d48', paddingBottom: 16, borderBottomLeftRadius: 24, borderBottomRightRadius: 24, elevation: 5, boxShadow: '0px 4px 6px rgba(225, 29, 72, 0.3)', zIndex: 50 },
  pageHeaderCushion: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'white', marginHorizontal: 16, marginTop: 16, marginBottom: 20, padding: 16, borderRadius: 20, elevation: 3, boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.05)', zIndex: 10 },
  scrollContent: { paddingHorizontal: 4, paddingBottom: 40 }, 
  headerTextWrap: { flex: 1, paddingRight: 10 },
  greeting: { fontSize: 14, color: '#64748b' },
  emailText: { fontSize: 20, fontWeight: 'bold', color: '#0f172a', textTransform: 'capitalize' },
  headerActions: { flexDirection: 'row', gap: 10 },
  profileButton: { padding: 10, backgroundColor: '#f1f5f9', borderRadius: 12 },
  logoutIconButton: { padding: 10, backgroundColor: '#fff1f2', borderRadius: 12 },
  heroSection: { backgroundColor: '#9f1239', borderRadius: 28, padding: 24, marginHorizontal: 16, marginBottom: 32, boxShadow: '0px 8px 24px rgba(159, 18, 57, 0.25)', elevation: 8 },
  heroHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  heroTitle: { fontSize: 24, fontWeight: 'bold', color: 'white', marginLeft: 10 },
  heroSubtitle: { color: '#fecdd3', fontSize: 14, marginBottom: 24 },
  toggleContainer: { flexDirection: 'row', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 16, padding: 4, marginBottom: 16 },
  toggleBtn: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 10, borderRadius: 12 },
  toggleBtnActive: { backgroundColor: '#e11d48', boxShadow: '0px 2px 4px rgba(0,0,0,0.1)' },
  toggleText: { color: '#fecdd3', fontWeight: '600', marginLeft: 6, fontSize: 14 },
  toggleTextActive: { color: 'white' },
  searchBox: { flexDirection: 'row', backgroundColor: 'white', borderRadius: 16, padding: 6, alignItems: 'center' },
  searchInput: { flex: 1, paddingHorizontal: 12, paddingVertical: 10, fontSize: 15, color: '#0f172a', minWidth: 0 },
  searchButton: { backgroundColor: '#0f172a', borderRadius: 12, paddingHorizontal: 16, paddingVertical: 10, flexDirection: 'row', alignItems: 'center', justifyContent: 'center' },
  searchButtonText: { color: 'white', fontWeight: 'bold', fontSize: 13 },
  aiResultWrapper: { marginBottom: 32, marginHorizontal: 16 },
  tabContainer: { flexDirection: 'row', backgroundColor: '#e2e8f0', borderRadius: 12, padding: 4, marginBottom: 16 },
  tabBtn: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 12, borderRadius: 10, gap: 8 },
  tabBtnActive: { backgroundColor: '#e11d48', elevation: 2, boxShadow: '0px 2px 5px rgba(225,29,72,0.3)' },
  tabBtnActiveBuy: { backgroundColor: '#4f46e5', elevation: 2, boxShadow: '0px 2px 5px rgba(79,70,229,0.3)' },
  tabText: { fontWeight: 'bold', color: '#64748b', fontSize: 14 },
  tabTextActive: { color: '#fff' },
  tabContentArea: { gap: 16 },
  matchContainer: { gap: 16 },
  wineCard: { backgroundColor: '#fff', padding: 20, borderRadius: 20, borderWidth: 1, borderColor: '#f1f5f9', boxShadow: '0px 4px 15px rgba(0,0,0,0.05)', elevation: 3 },
  altWineCard: { backgroundColor: '#fafaf9' },
  matchBadge: { flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-start', backgroundColor: '#16a34a', paddingHorizontal: 10, paddingVertical: 4, borderRadius: 8, marginBottom: 12, gap: 4 },
  matchBadgeText: { color: '#fff', fontWeight: 'bold', fontSize: 12 },
  altBadgeText: { color: '#64748b', fontWeight: 'bold', fontSize: 12, marginBottom: 8, textTransform: 'uppercase' },
  wineCardTitle: { fontSize: 20, fontWeight: 'bold', color: '#0f172a', marginBottom: 8 },
  wineCardReason: { fontSize: 15, color: '#475569', lineHeight: 22 },
  actionButtonsRow: { flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', marginTop: 16, gap: 10 },
  locateWineBtn: { flexDirection: 'row', alignItems: 'center', paddingVertical: 8, paddingHorizontal: 12, backgroundColor: '#f1f5f9', borderRadius: 10, borderWidth: 1, borderColor: '#e2e8f0' },
  locateWineBtnText: { color: '#475569', fontWeight: 'bold', fontSize: 13, marginLeft: 6 },
  openWineBtn: { flexDirection: 'row', alignItems: 'center', paddingVertical: 8, paddingHorizontal: 12, backgroundColor: '#fff1f2', borderRadius: 10, gap: 6 },
  openWineBtnText: { color: '#e11d48', fontWeight: 'bold', fontSize: 14 },
  noMatchCard: { backgroundColor: '#fff1f2', padding: 24, borderRadius: 20, alignItems: 'center', borderWidth: 1, borderColor: '#ffe4e6' },
  noMatchTitle: { fontSize: 18, fontWeight: 'bold', color: '#be123c', marginTop: 12, marginBottom: 8 },
  noMatchText: { fontSize: 15, color: '#9f1239', textAlign: 'center', lineHeight: 22 },
  buyCard: { backgroundColor: '#fff', padding: 20, borderRadius: 20, borderWidth: 1, borderColor: '#e0e7ff', borderLeftWidth: 4, borderLeftColor: '#4f46e5', boxShadow: '0px 4px 15px rgba(0,0,0,0.05)', elevation: 3, marginBottom: 16 },
  buyIconWrap: { width: 40, height: 40, borderRadius: 10, backgroundColor: '#e0e7ff', alignItems: 'center', justifyContent: 'center', marginBottom: 12 },
  buyCardTitle: { fontSize: 18, fontWeight: 'bold', color: '#312e81', marginBottom: 8 },
  aiResultContainer: { backgroundColor: 'white', borderRadius: 24, padding: 24, marginHorizontal: 16, marginBottom: 32, borderWidth: 1, borderColor: '#e2e8f0', boxShadow: '0px 4px 12px rgba(0,0,0,0.05)', elevation: 3 },
  aiResultTitle: { fontSize: 16, fontWeight: 'bold', color: '#0f172a', marginBottom: 8 },
  aiResultText: { fontSize: 15, color: '#475569', lineHeight: 22, marginBottom: 20 },
  matchedTitle: { fontSize: 14, fontWeight: 'bold', color: '#e11d48', marginBottom: 16 },
  foodCard: { flexDirection: 'row', backgroundColor: '#f8fafc', padding: 12, borderRadius: 16, marginBottom: 12 },
  foodIconBg: { backgroundColor: '#ffe4e6', padding: 10, borderRadius: 12, marginRight: 12, alignSelf: 'flex-start' },
  foodInfo: { flex: 1 },
  foodName: { fontSize: 16, fontWeight: 'bold', color: '#0f172a', marginBottom: 4 },
  foodReason: { fontSize: 14, color: '#64748b', lineHeight: 20 },
  sectionTitle: { fontSize: 18, fontWeight: 'bold', color: '#0f172a', marginBottom: 16, marginLeft: 20 },
  gridContainer: { marginHorizontal: 16 },
  gridCardFull: { flexDirection: 'row', alignItems: 'center', backgroundColor: 'white', padding: 16, borderRadius: 20, elevation: 2, boxShadow: '0px 2px 8px rgba(0,0,0,0.04)', marginBottom: 12 },
  gridIconWrap: { padding: 12, borderRadius: 14 },
  gridTitleFull: { fontSize: 16, fontWeight: 'bold', color: '#0f172a', marginBottom: 2 },
  gridSubFull: { fontSize: 13, color: '#64748b' },
  gridRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 12 },
  gridCardHalf: { width: '48%', backgroundColor: 'white', borderRadius: 20, padding: 16, elevation: 2, boxShadow: '0px 2px 8px rgba(0,0,0,0.04)' },
  gridCardTop: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
  gridTitle: { fontSize: 16, fontWeight: 'bold', color: '#0f172a', marginBottom: 4 },
  gridSub: { fontSize: 12, color: '#64748b', lineHeight: 16 },
  alertBadge: { backgroundColor: '#e11d48', width: 22, height: 22, borderRadius: 11, alignItems: 'center', justifyContent: 'center' },
  alertBadgeText: { color: 'white', fontSize: 12, fontWeight: 'bold' },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(15, 23, 42, 0.6)', justifyContent: 'center', alignItems: 'center', padding: 20 },
  modalBox: { backgroundColor: 'white', borderRadius: 24, padding: 24, width: '100%', maxWidth: 400, boxShadow: '0px 10px 25px rgba(0,0,0,0.2)', elevation: 10 },
  modalHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 16 },
  modalTitle: { fontSize: 20, fontWeight: 'bold', color: '#0f172a', marginLeft: 12, flex: 1 },
  modalMessage: { fontSize: 16, color: '#475569', lineHeight: 24, marginBottom: 24 },
  modalButtons: { flexDirection: 'row', justifyContent: 'flex-end', gap: 12 },
  modalCancelBtn: { paddingVertical: 12, paddingHorizontal: 20, borderRadius: 12, backgroundColor: '#f1f5f9' },
  modalCancelText: { color: '#475569', fontWeight: 'bold', fontSize: 16 },
  modalConfirmBtn: { paddingVertical: 12, paddingHorizontal: 20, borderRadius: 12, minWidth: 100, alignItems: 'center' },
  modalConfirmText: { color: 'white', fontWeight: 'bold', fontSize: 16 }
});